import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { MatRadioModule } from '@angular/material/radio';

import { PessoasService } from '../../../services/pessoa/pessoas.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { AuthService } from '../../../services/auth/auth.service';

export interface RegistroConsultaExameRequest {
  id_pessoa: number;
  data_consulta: string;
  horario_consulta: string;
  nome_profissional: string;
  apontamentos?: string;
  sf36?: {
    capacidade_funcional?: number | null;
    dor?: number | null;
    vitalidade?: number | null;
    limitacao_aspectos_emocionais?: number | null;
    limitacao_aspectos_fisico?: number | null;
    estado_geral_saude?: number | null;
    aspectos_sociais?: number | null;
    saude_mental?: number | null;
  };
  movia?: {
    classificacao_eva?: number | null;
    frequencia_dor?: number | null;
    melhora_postura?: number | null;
  };
  equilibria?: {
    peso?: number | null;
    circunferencia_abdominal?: number | null;
    frequencia_atividade_fisica?: number | null;
  };
}

export interface RegistroConsultaExameResponse {
  idRegistroConsultaExame: number;
  idPessoa: number;
  dataConsulta: string;
  horarioConsulta: string;
  nomeProfissional: string;
  apontamentos?: string;
  sf36?: {
    capacidadeFuncional?: number | null;
    dor?: number | null;
    vitalidade?: number | null;
    limitacaoAspectosEmocionais?: number | null;
    limitacaoAspectosFisico?: number | null;
    estadoGeralSaude?: number | null;
    aspectosSociais?: number | null;
    saudeMental?: number | null;
  };
  movia?: {
    classificacaoEva?: number | null;
    frequenciaDor?: number | null;
    melhoraPostura?: number | null;
  };
  equilibria?: {
    peso?: number | null;
    circunferenciaAbdominal?: number | null;
    frequenciaAtividadeFisica?: number | null;
  };
}

@Component({
  selector: 'app-consultas-exames',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    NgxMaskDirective,
    MatRadioModule,
  ],
  templateUrl: './consultas-exames.html',
  styleUrl: './consultas-exames.scss',
  providers: [provideNgxMask()],
})
export class ConsultasExames implements OnInit {
  pessoaId!: number;
  registros: RegistroConsultaExameResponse[] = [];
  carregando = false;
  mostrandoFormulario = false;
  modoEdicao = false;
  registroEmEdicao: RegistroConsultaExameResponse | null = null;
  salvando = false;
  submitted = false;
  @Input() linhaDeCuidadoId?: number;
  @Input() linhaDeCuidadoNome?: string;

  dataConsulta = '';
  horarioConsulta = '';
  nomeProfissional = '';
  apontamentos = '';

  equilibriaPeso?: number | string | null;
  equilibriaCircunferenciaAbdominal?: number | string | null;
  frequenciaAtividadeFisica?: number | null;

  sf36CapacidadeFuncional?: number | null;
  sf36Dor?: number | null;
  sf36Vitalidade?: number | null;
  sf36LimitacaoAspectosEmocionais?: number | null;
  sf36LimitacaoAspectosFisico?: number | null;
  sf36EstadoGeralSaude?: number | null;
  sf36AspectosSociais?: number | null;
  sf36SaudeMental?: number | null;

  moviaClassificacaoEva?: number | null;
  moviaFrequenciaDor?: number | null;
  moviaMelhoraPostura?: number | null;

  erroData = signal('');
  erroHorario = signal('');
  erroProfissional = signal('');

  // E possivel adicionar, caso no futuro tenha mais
  frequenciaAtividadeFisicaOptions = [
    { value: 1, label: 'Não pratica atividade física' },
    { value: 2, label: 'Até 15 minutos' },
    { value: 3, label: 'Até 30 minutos' },
    { value: 4, label: 'Até 45 minutos' },
    { value: 5, label: 'Mais de 45 minutos' },
  ];

  // E possivel adicionar, caso no futuro tenha mais
  moviaFrequenciaDorOptions = [
    { value: 1, label: 'Sem dor (ausência de episódios)' },
    { value: 2, label: 'Leve' },
    { value: 3, label: 'Moderada' },
    { value: 4, label: 'Frequente' },
    { value: 5, label: 'Contínua (dor diária ou constante)' },
  ];

 // E possivel adicionar, caso no futuro tenha mais
  moviaMelhoraPosturaOptions = [
    { value: 1, label: 'Melhorou' },
    { value: 2, label: 'Manteve' },
    { value: 3, label: 'Piorou' },
    { value: 4, label: 'Não avaliado' },
  ];

  constructor(
    private route: ActivatedRoute,
    private pessoasService: PessoasService,
    private snackbarService: SnackbarService,
    private authService: AuthService,
  ) {}

  async ngOnInit(): Promise<void> {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.pessoaId = +id;
      await this.buscarRegistros();
    }
  }

  formBloqueado(): boolean {
    const perfisQuePodemEditar = ['ROLE_MASTER', 'ROLE_SAUDE'];
    const perfil = this.authService.getPerfil();
    return !perfisQuePodemEditar.includes(perfil);
  }

  async buscarRegistros(): Promise<void> {
    if (!this.pessoaId) return;
    this.carregando = true;
    try {
      this.registros =
        await this.pessoasService.listarRegistrosConsultaExame(this.pessoaId);
    } catch {
      this.snackbarService.error('Erro ao buscar registros de consultas');
    } finally {
      this.carregando = false;
    }
  }

  abrirNovoRegistro(): void {
    this.modoEdicao = false;
    this.registroEmEdicao = null;
    this.submitted = false;
    this.resetFormulario();
    this.mostrandoFormulario = true;
    // Garante que não venha preenchido por autofill do navegador
    setTimeout(() => {
      this.dataConsulta = '';
      this.horarioConsulta = '';
    }, 0);
  }

  private toISODate(data: string): string {
    const raw = (data || '').trim();
    if (!raw) return '';
    if (raw.includes('/')) {
      const [dia, mes, ano] = raw.split('/');
      if (!dia || !mes || !ano) return '';
      if (dia.length !== 2 || mes.length !== 2 || ano.length !== 4) return '';
      return `${ano}-${mes}-${dia}`;
    }
    const digits = raw.replace(/\D/g, '');
    if (digits.length !== 8) return '';
    const dia = digits.slice(0, 2);
    const mes = digits.slice(2, 4);
    const ano = digits.slice(4, 8);
    return `${ano}-${mes}-${dia}`;
  }

  private fromISODate(data: string): string {
    if (!data) return '';
    const iso = data.split('T')[0];
    const [ano, mes, dia] = iso.split('-');
    if (!dia || !mes || !ano) return '';
    return `${dia}/${mes}/${ano}`;
  }

  private normalizeTime(value: string): string {
    const raw = (value || '').trim();
    if (!raw) return '';
    if (raw.includes(':')) return raw;
    const digits = raw.replace(/\D/g, '');
    if (digits.length !== 4) return '';
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  }

  private isFutureDateBR(data: string): boolean {
    const iso = this.toISODate(data);
    if (!iso) return false;
    const [ano, mes, dia] = iso.split('-').map(Number);
    if (!ano || !mes || !dia) return false;
    const d = new Date(ano, mes - 1, dia, 0, 0, 0, 0);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return d > hoje;
  }

  private isValidTimeHHMM(value: string): boolean {
    const norm = this.normalizeTime(value);
    if (!norm) return false;
    const [h, m] = norm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return false;
    return h >= 0 && h <= 23 && m >= 0 && m <= 59;
  }

  get linhaDeCuidadoLabel(): string {
    if (this.linhaDeCuidadoId === 1) return 'Dor lombar';
    if (this.linhaDeCuidadoId === 2) return 'Sobrepeso';
    return this.linhaDeCuidadoNome || '';
  }

  get modoLinhaCuidado(): 'LOMBAR' | 'SOBREPESO' | '' {
    if (this.linhaDeCuidadoId === 1) return 'LOMBAR';
    if (this.linhaDeCuidadoId === 2) return 'SOBREPESO';
    return '';
  }

  editarRegistro(registro: RegistroConsultaExameResponse): void {
    this.modoEdicao = true;
    this.registroEmEdicao = registro;
    this.submitted = false;
    this.preencherDados(registro);
    this.mostrandoFormulario = true;
  }

  formatarData(data?: string): string {
    if (!data) return '';
    const [ano, mes, dia] = data.split('-');
    if (!ano || !mes || !dia) return data;
    return `${dia}/${mes}/${ano}`;
  }

  cancelar(): void {
    this.cancelarFormulario();
  }

  cancelarFormulario(): void {
    this.mostrandoFormulario = false;
    this.registroEmEdicao = null;
    this.modoEdicao = false;
    this.submitted = false;
    this.resetFormulario();
  }

  private resetFormulario(): void {
    this.dataConsulta = '';
    this.horarioConsulta = '';
    this.nomeProfissional = '';
    this.apontamentos = '';

    this.equilibriaPeso = null;
    this.equilibriaCircunferenciaAbdominal = null;
    this.frequenciaAtividadeFisica = null;

    this.sf36CapacidadeFuncional = null;
    this.sf36Dor = null;
    this.sf36Vitalidade = null;
    this.sf36LimitacaoAspectosEmocionais = null;
    this.sf36LimitacaoAspectosFisico = null;
    this.sf36EstadoGeralSaude = null;
    this.sf36AspectosSociais = null;
    this.sf36SaudeMental = null;

    this.moviaClassificacaoEva = null;
    this.moviaFrequenciaDor = null;
    this.moviaMelhoraPostura = null;

    this.erroData.set('');
    this.erroHorario.set('');
    this.erroProfissional.set('');
  }

  private preencherDados(registro: RegistroConsultaExameResponse): void {
    this.dataConsulta = this.fromISODate(registro.dataConsulta || '');
    this.horarioConsulta = registro.horarioConsulta || '';
    this.nomeProfissional = registro.nomeProfissional || '';
    this.apontamentos = registro.apontamentos || '';

    this.equilibriaPeso = registro.equilibria?.peso ?? null;
    this.equilibriaCircunferenciaAbdominal =
      registro.equilibria?.circunferenciaAbdominal ?? null;
    this.frequenciaAtividadeFisica =
      registro.equilibria?.frequenciaAtividadeFisica ?? null;

    this.sf36CapacidadeFuncional = registro.sf36?.capacidadeFuncional ?? null;
    this.sf36Dor = registro.sf36?.dor ?? null;
    this.sf36Vitalidade = registro.sf36?.vitalidade ?? null;
    this.sf36LimitacaoAspectosEmocionais =
      registro.sf36?.limitacaoAspectosEmocionais ?? null;
    this.sf36LimitacaoAspectosFisico =
      registro.sf36?.limitacaoAspectosFisico ?? null;
    this.sf36EstadoGeralSaude = registro.sf36?.estadoGeralSaude ?? null;
    this.sf36AspectosSociais = registro.sf36?.aspectosSociais ?? null;
    this.sf36SaudeMental = registro.sf36?.saudeMental ?? null;

    this.moviaClassificacaoEva = registro.movia?.classificacaoEva ?? null;
    this.moviaFrequenciaDor = registro.movia?.frequenciaDor ?? null;
    this.moviaMelhoraPostura = registro.movia?.melhoraPostura ?? null;
  }

  private validarObrigatorios(): boolean {
    this.erroData.set('');
    this.erroHorario.set('');
    this.erroProfissional.set('');

    let valido = true;

    const dataIso = this.toISODate(this.dataConsulta);
    if (!this.dataConsulta || !dataIso) {
      this.erroData.set('Data da consulta é obrigatória');
      this.snackbarService.error('Preencha a data da consulta');
      valido = false;
    } else if (this.isFutureDateBR(this.dataConsulta)) {
      this.erroData.set('Data da consulta não pode ser futura');
      this.snackbarService.error('Data da consulta não pode ser futura');
      valido = false;
    }

    const horaNorm = this.normalizeTime(this.horarioConsulta);
    if (!this.horarioConsulta || !horaNorm) {
      this.erroHorario.set('Horário da consulta é obrigatório');
      this.snackbarService.error('Preencha o horário da consulta');
      valido = false;
    } else if (!this.isValidTimeHHMM(this.horarioConsulta)) {
      this.erroHorario.set('Horário inválido');
      this.snackbarService.error('Horário inválido');
      valido = false;
    }

    if (!this.nomeProfissional?.trim()) {
      this.erroProfissional.set('Profissional responsável é obrigatório');
      this.snackbarService.error('Preencha o profissional responsável');
      valido = false;
    }

    if (!this.apontamentos?.trim()) {
      this.snackbarService.error('Apontamentos não pode ser vazio');
      valido = false;
    }

    return valido;
  }

  private toNumber(value?: number | string | null): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return value;
    const normalized = value
      .toString()
      .replace(/\./g, '')
      .replace(',', '.');
    const parsed = Number(normalized);
    return Number.isNaN(parsed) ? null : parsed;
  }

  formatDecimal2(
    field: 'equilibriaPeso' | 'equilibriaCircunferenciaAbdominal',
  ): void {
    const current = this[field];
    if (current === null || current === undefined) return;
    if (typeof current === 'string' && current.trim() === '') return;
    const num = this.toNumber(current);
    if (num === null) return;
    const formatted = num.toFixed(2).replace('.', ',');
    this[field] = formatted;
  }

  private buildPayload(): RegistroConsultaExameRequest {
    const sf36 = {
      capacidade_funcional: this.toNumber(this.sf36CapacidadeFuncional),
      dor: this.toNumber(this.sf36Dor),
      vitalidade: this.toNumber(this.sf36Vitalidade),
      limitacao_aspectos_emocionais: this.toNumber(
        this.sf36LimitacaoAspectosEmocionais,
      ),
      limitacao_aspectos_fisico: this.toNumber(
        this.sf36LimitacaoAspectosFisico,
      ),
      estado_geral_saude: this.toNumber(this.sf36EstadoGeralSaude),
      aspectos_sociais: this.toNumber(this.sf36AspectosSociais),
      saude_mental: this.toNumber(this.sf36SaudeMental),
    };

    const movia = {
      classificacao_eva: this.toNumber(this.moviaClassificacaoEva),
      frequencia_dor: this.toNumber(this.moviaFrequenciaDor),
      melhora_postura: this.toNumber(this.moviaMelhoraPostura),
    };

    const equilibria = {
      peso: this.toNumber(this.equilibriaPeso),
      circunferencia_abdominal: this.toNumber(
        this.equilibriaCircunferenciaAbdominal,
      ),
      frequencia_atividade_fisica: this.toNumber(
        this.frequenciaAtividadeFisica,
      ),
    };

    const hasSf36 = Object.values(sf36).some((v) => v !== null);
    const hasMovia = Object.values(movia).some((v) => v !== null);
    const hasEquilibria = Object.values(equilibria).some((v) => v !== null);

    return {
      id_pessoa: this.pessoaId,
      data_consulta: this.toISODate(this.dataConsulta),
      horario_consulta: this.normalizeTime(this.horarioConsulta),
      nome_profissional: this.nomeProfissional,
      apontamentos: this.apontamentos || undefined,
      sf36: hasSf36 ? sf36 : undefined,
      movia: hasMovia ? movia : undefined,
      equilibria: hasEquilibria ? equilibria : undefined,
    };
  }

  async salvar(): Promise<void> {
    this.submitted = true;
    if (!this.validarObrigatorios()) return;

    this.salvando = true;
    try {
      const payload = this.buildPayload();
      if (this.modoEdicao && this.registroEmEdicao?.idRegistroConsultaExame) {
        await this.pessoasService.atualizarRegistroConsultaExame(
          this.registroEmEdicao.idRegistroConsultaExame,
          payload,
        );
        this.snackbarService.success('Registro atualizado com sucesso');
      } else {
        await this.pessoasService.salvarRegistroConsultaExame(payload);
        this.snackbarService.success('Registro salvo com sucesso');
      }

      await this.buscarRegistros();
      this.cancelarFormulario();
    } catch {
      this.snackbarService.error('Erro ao salvar registro');
    } finally {
      this.salvando = false;
    }
  }
}

